export interface QueuedStream {
  userId: string;
  userLogin: string;
  userName: string;
  profileImageUrl: string;
  title: string;
  startedAt: string;
  viewerCount: number;
  thumbnailUrl: string;
  queuePosition: number;
  activeSlotId?: string;  // Optional field to identify r3dlabs-sourced entries
  eventName?: string;     // Optional field to store r3dlabs event name
  isLive?: boolean;       // Optional field to track stream status
  wasRaided?: boolean;    // Optional field to track if the stream was raided
}

interface RaidHistory {
  activeSlotId: string;
  timestamp: number;
}

export class RaidQueueService {
  private static readonly QUEUE_STORAGE_KEY = 'raid_queue';
  private static readonly RAID_HISTORY_KEY = 'raid_history';
  private queuedStreams: QueuedStream[] = [];
  private raidHistory: RaidHistory[] = [];

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    // Load queue
    const storedQueue = localStorage.getItem(RaidQueueService.QUEUE_STORAGE_KEY);
    if (storedQueue) {
      try {
        this.queuedStreams = JSON.parse(storedQueue);
        this.queuedStreams.sort((a, b) => a.queuePosition - b.queuePosition);
      } catch (error) {
        console.error('Failed to load raid queue:', error);
        this.queuedStreams = [];
      }
    }

    // Load raid history
    const storedHistory = localStorage.getItem(RaidQueueService.RAID_HISTORY_KEY);
    if (storedHistory) {
      try {
        this.raidHistory = JSON.parse(storedHistory);
      } catch (error) {
        console.error('Failed to load raid history:', error);
        this.raidHistory = [];
      }
    }
  }

  private saveToStorage(): void {
    localStorage.setItem(RaidQueueService.QUEUE_STORAGE_KEY, JSON.stringify(this.queuedStreams));
    localStorage.setItem(RaidQueueService.RAID_HISTORY_KEY, JSON.stringify(this.raidHistory));
  }

  public getQueue(): QueuedStream[] {
    return [...this.queuedStreams];
  }

  public getFilteredQueue(excludeR3Labs: boolean = false): QueuedStream[] {
    return this.queuedStreams.filter(stream => {
      if (excludeR3Labs && stream.eventName !== undefined) {
        return false;
      }
      return true;
    });
  }

  public addToQueue(stream: Omit<QueuedStream, 'queuePosition'>): void {
    // Check if already in queue
    if (this.queuedStreams.some(s => s.userId === stream.userId)) {
      return;
    }

    // Add to end of queue
    const queuePosition = this.queuedStreams.length > 0 
      ? Math.max(...this.queuedStreams.map(s => s.queuePosition)) + 1 
      : 0;

    this.queuedStreams.push({ ...stream, queuePosition });
    this.saveToStorage();
    this.dispatchQueueUpdate();
  }

  public removeFromQueue(userId: string): void {
    const wasRemoved = this.queuedStreams.some(s => s.userId === userId);
    this.queuedStreams = this.queuedStreams.filter(s => s.userId !== userId);
    
    if (wasRemoved) {
      this.saveToStorage();
      this.dispatchQueueUpdate();
    }
  }

  public moveToPosition(userId: string, newIndex: number): void {
    const streamIndex = this.queuedStreams.findIndex(s => s.userId === userId);
    if (streamIndex === -1) return;

    // Ensure newIndex is within bounds
    newIndex = Math.max(0, Math.min(newIndex, this.queuedStreams.length - 1));

    // Remove the stream from its current position
    const [stream] = this.queuedStreams.splice(streamIndex, 1);

    // Insert at new position
    this.queuedStreams.splice(newIndex, 0, stream);

    // Update all positions sequentially
    this.queuedStreams.forEach((s, index) => {
      s.queuePosition = index;
    });

    this.saveToStorage();
    this.dispatchQueueUpdate();
  }

  public isQueued(userId: string): boolean {
    return this.queuedStreams.some(s => s.userId === userId);
  }

  public markAsRaided(userId: string): void {
    const stream = this.queuedStreams.find(s => s.userId === userId);
    if (stream) {
      stream.wasRaided = true;
      if (stream.activeSlotId) {
        // Only add to raid history when explicitly raided
        this.raidHistory.push({
          activeSlotId: stream.activeSlotId,
          timestamp: Date.now()
        });
      }
      this.saveToStorage();
      this.dispatchQueueUpdate();
    }
  }

  public updateStreamLiveStatus(liveStreamIds: Set<string>): void {
    let updated = false;
    
    this.queuedStreams.forEach(stream => {
      const isLive = liveStreamIds.has(stream.userId);
      if (stream.isLive !== isLive) {
        stream.isLive = isLive;
        updated = true;
      }
    });

    // Remove offline streams that aren't r3labs entries
    this.queuedStreams = this.queuedStreams.filter(stream => 
      stream.isLive || stream.activeSlotId !== undefined
    );

    if (updated) {
      this.saveToStorage();
      this.dispatchQueueUpdate();
    }
  }

  public clearQueue(): void {
    if (this.queuedStreams.length > 0) {
      this.queuedStreams = [];
      this.saveToStorage();
      this.dispatchQueueUpdate();
    }
  }

  public wasSlotRaided(activeSlotId: string): boolean {
    return this.raidHistory.some(entry => entry.activeSlotId === activeSlotId);
  }

  public cleanupOldHistory(): void {
    const ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const now = Date.now();
    const oldLength = this.raidHistory.length;
    
    this.raidHistory = this.raidHistory.filter(entry => {
      return now - entry.timestamp < ONE_DAY;
    });

    if (this.raidHistory.length !== oldLength) {
      this.saveToStorage();
    }
  }

  private dispatchQueueUpdate(): void {
    document.dispatchEvent(new CustomEvent('queueupdate'));
  }
}

export const raidQueueService = new RaidQueueService();
